<template>
  <section class="col-span-full">
    <h3 class="text-base font-semibold leading-6 text-gray-900 flex items-center">
      <span>Plugins</span>
      <span class="ml-2 text-sm font-normal text-gray-500" aria-live="polite">
        {{ selectedPlugins.length }} selected
      </span>
    </h3>
    <p class="mt-1 text-sm text-gray-500">
      Link one or more plugins to this program. You can pick existing plugins or create a new one.
    </p>

    <!-- Search and select existing -->
    <div class="mt-4 grid gap-3 md:grid-cols-3">
      <div class="md:col-span-2">
        <label for="plugin-search" class="block text-sm font-medium text-gray-700">Find plugin</label>
        <div class="mt-1 relative">
          <input
            id="plugin-search"
            v-model.trim="search"
            type="search"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            placeholder="Search plugins..."
            :disabled="disabled"
            autocomplete="off"
            aria-controls="plugin-options"
            :aria-expanded="Boolean(searchResults.length && search.length)"
          >

          <ul
            v-if="searchResults.length && search.length"
            id="plugin-options"
            role="listbox"
            class="absolute z-10 mt-1 max-h-60 w-full overflow-auto
              rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5
              focus:outline-none sm:text-sm"
          >
            <li
              v-for="p in searchResults"
              :key="p.id"
              role="option"
              class="select-none px-3 py-2 hover:bg-gray-50 flex items-center justify-between"
              :class="isSelected(p.id) ? 'cursor-not-allowed' : 'cursor-pointer'"
              tabindex="0"
              :aria-selected="isSelected(p.id)"
              @click="addPlugin(p)"
              @keydown.enter.prevent="addPlugin(p)"
            >
              <div :class="{ 'opacity-40': !p.isEnabled , 'cursor-not-allowed': isSelected(p.id)}">
                <div class="text-gray-900">
                  {{ p.name }}
                </div>
                <div class="text-gray-500 text-xs truncate">
                  {{ p.info || 'No description' }}
                </div>
              </div>
              <button
                class="ml-3 text-primary-600 hover:text-primary-700 text-sm"
                type="button"
                :disabled="disabled || isSelected(p.id)"
                aria-label="Add plugin"
                @click.stop="addPlugin(p)"
              >
                Add
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div class="flex items-end">
        <button
          type="button"
          class="inline-flex items-center rounded-md border border-primary-600
            px-3 py-2.5 text-sm font-semibold text-primary-700
          hover:bg-primary-50 w-full md:w-auto"
          :class="{ 'opacity-50 pointer-events-none': disabled }"
          :aria-expanded="toggleCreate"
          :aria-controls="createFormId"
          @click="toggleCreate = !toggleCreate"
        >
          <PlusIcon class="size-5 -ml-1 mr-2" />
          New plugin
        </button>
      </div>
    </div>

    <!-- Selected chips -->
    <div class="mt-4">
      <ul class="flex flex-wrap gap-2" aria-live="polite" aria-label="Selected plugins">
        <li
          v-for="p in selectedPlugins"
          :key="p.id"
          class="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm"
          :class="p.isEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
        >
          <span class="truncate max-w-60" :title="p.name">{{ p.name }}</span>
          <button
            type="button"
            class="cursor-pointer"
            :class="{
              'pointer-events-none opacity-50': disabled,
              'text-green-800 hover:text-green-900': p.isEnabled,
              'text-red-800 hover:text-red-900': !p.isEnabled

            }"
            :aria-label="`Remove ${p.name}`"
            @click="removePlugin(p.id)"
          >
            <XMarkIcon class="size-4" />
          </button>
        </li>
      </ul>
      <p v-if="!selectedPlugins.length" class="text-sm text-gray-500 mt-2">
        No plugins selected yet.
      </p>
    </div>

    <!-- Create new plugin -->
    <div
      v-show="toggleCreate"
      :id="createFormId"
      class="mt-6 rounded-md border border-gray-200 p-4"
      aria-live="polite"
    >
      <h4 class="text-sm font-medium text-gray-900 mb-3">
        Add new plugin
      </h4>

      <div class="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div class="md:col-span-3">
          <label for="np-name" class="block text-sm font-medium text-gray-700">Name</label>
          <input
            id="np-name"
            v-model.trim="newPlugin.name"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 mt-1"
            :disabled="creating || disabled"
          >
          <p v-if="errors.name" class="mt-1 text-xs text-red-600">
            {{ errors.name }}
          </p>
        </div>

        <div class="md:col-span-3">
          <label for="np-appid" class="block text-sm font-medium text-gray-700">Automation App Id</label>
          <input
            id="np-appid"
            v-model.number="newPlugin.automationAppId"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 mt-1"
            type="number"
            min="0"
            :disabled="creating || disabled"
          >
          <p v-if="errors.automationAppId" class="mt-1 text-xs text-red-600">
            {{ errors.automationAppId }}
          </p>
        </div>

        <div class="md:col-span-3">
          <label for="np-info" class="block text-sm font-medium text-gray-700">Short description</label>
          <textarea
            id="np-info"
            v-model.trim="newPlugin.info"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 mt-1"
            rows="3"
            :disabled="creating || disabled"
          />
          <p v-if="errors.info" class="mt-1 text-xs text-red-600">
            {{ errors.info }}
          </p>
        </div>

        <div class="md:col-span-3">
          <label for="np-desc" class="block text-sm font-medium text-gray-700">Long description</label>
          <textarea
            id="np-desc"
            v-model.trim="newPlugin.description"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 mt-1"
            rows="3"
            :disabled="creating || disabled"
          />
          <p v-if="errors.description" class="mt-1 text-xs text-red-600">
            {{ errors.description }}
          </p>
        </div>

        <div class="md:col-span-2 flex items-center gap-2">
          <input
            id="np-exe"
            v-model="newPlugin.isExecutable"
            type="checkbox"
            class="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            :disabled="creating || disabled"
          >
          <label for="np-exe" class="text-sm text-gray-700">Executable</label>
        </div>

        <div class="md:col-span-4 text-right">
          <button
            type="button"
            class="inline-flex items-center rounded-md border border-primary-600 px-3 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-50 mr-2"
            :disabled="creating || disabled"
            @click="resetCreate"
          >
            Cancel
          </button>
          <button
            type="button"
            class="inline-flex items-center rounded-md bg-primary-600
              px-3 py-2 text-sm font-semibold text-white shadow-sm
              hover:bg-primary-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
            :class="{ 'opacity-50 pointer-events-none': creating || disabled }"
            @click="createPlugin"
          >
            <span v-if="creating">Creating…</span>
            <span v-else>Create & add</span>
          </button>
        </div>
      </div>
    </div>

    <span class="sr-only" role="status" aria-live="polite">{{ liveMessage }}</span>
  </section>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useDebounceFn, useFetch } from '@vueuse/core';
import { useToast } from 'vue-toastification';
import { PlusIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { apiDomain } from '../composables/useConstants';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([ 'update:modelValue' ]);

const selectedPlugins = computed({
  get: () => props.modelValue || [],
  set: val => emit('update:modelValue', val)
});

const search = ref('');
const searchResults = ref([]);
const searching = ref(false);
let requestId = 0;
const creating = ref(false);
const toggleCreate = ref(false);
const createFormId = `create-plugin-${Math.random().toString(36).slice(2)}`;
const toast = useToast();
const liveMessage = ref('');

// new plugin form
const newPlugin = reactive({
  name: '',
  automationAppId: null,
  description: '',
  info: '',
  isExecutable: false
});
const errors = reactive({
  name: '',
  automationAppId: '',
  description: '',
  info: ''
});

function isSelected(id) {
  return selectedPlugins.value?.some(p => p.id === id);
}

function addPlugin(plugin) {
  if (props.disabled || !plugin || isSelected(plugin.id)) {
    return;
  }
  selectedPlugins.value = [ ...(selectedPlugins.value || []), pickPluginFields(plugin) ];
  liveMessage.value = `Added plugin ${plugin.name}`;
  search.value = '';
  searchResults.value = [];
}

function removePlugin(id) {
  if (props.disabled) {
    return;
  }
  const removed = (selectedPlugins.value || []).find(p => p.id === id);
  selectedPlugins.value = (selectedPlugins.value || []).filter(p => p.id !== id);
  if (removed) liveMessage.value = `Removed plugin ${removed.name}`;
}

function pickPluginFields(p) {
  const { id, automationAppId, name, description, info, isExecutable, isEnabled } = p;
  return { id, automationAppId, name, description, info, isExecutable, isEnabled };
}

const runSearch = useDebounceFn(async () => {
  const q = search.value.trim();
  if (!q) {
    searchResults.value = [];
    return;
  }

  searching.value = true;
  const myId = ++requestId;

  try {
    const url = `${apiDomain}/api/plugin/?search=${encodeURIComponent(q)}&limit=50`;
    const { data, error } = await useFetch(url).get().json();

    if (myId !== requestId) {
      return;
    }

    searching.value = false;

    if (error.value) {
      searchResults.value = [];
      return;
    }

    const list = data.value?.plugins || [];
    searchResults.value = list.map(pickPluginFields);
  } catch {
    if (myId === requestId) {
      searching.value = false;
      searchResults.value = [];
    }
  }
}, 250);

watch(search, () => {
  if (props.disabled) {
    return;
  }
  runSearch();
});

function validateNew() {
  errors.name = newPlugin.name ? '' : 'Name is required';
  errors.automationAppId = Number.isFinite(newPlugin.automationAppId) ? '' : 'Automation App Id is required';
  errors.description = newPlugin.description ? '' : 'Description is required';
  errors.info = newPlugin.info ? '' : 'Info is required';
  return !(errors.name || errors.automationAppId || errors.description || errors.info);
}

function resetCreate() {
  toggleCreate.value = false;
  creating.value = false;
  newPlugin.name = '';
  newPlugin.automationAppId = null;
  newPlugin.description = '';
  newPlugin.info = '';
  newPlugin.isExecutable = false;
  Object.keys(errors).forEach(k => errors[k] = '');
}

async function createPlugin() {
  if (props.disabled || creating.value) {
    return;
  }
  if (!validateNew()) {
    return;
  }

  creating.value = true;
  try {
    const payload = {
      name: newPlugin.name.trim(),
      automationAppId: newPlugin.automationAppId,
      description: newPlugin.description.trim(),
      info: newPlugin.info.trim(),
      isExecutable: !!newPlugin.isExecutable
    };

    const { data, error } = await useFetch(`${apiDomain}/api/plugin`).post(payload).json();
    creating.value = false;

    if (error.value) {
      toast.error(typeof error.value === 'string' ? error.value : 'Failed to create plugin');
      return;
    }

    const created = data.value;
    if (!created?.id) {
      toast.error('Unexpected response when creating plugin');
      return;
    }

    const normalized = pickPluginFields(created);
    addPlugin(normalized);
    toast.success('Plugin created and added');
    resetCreate();
  } catch (e) {
    console.log(e);
    creating.value = false;
    toast.error('Failed to create plugin');
  }
}

</script>
